import { createSlice } from '@reduxjs/toolkit';
import { DateTime, Duration } from 'luxon';

function startOfDay(nDaysFromToday) {
  const today = DateTime.local().startOf('day');
  const delta = Duration.fromObject({ days: nDaysFromToday });
  return today.plus(delta);
}

function yesterday() {
  return startOfDay(-1);
}

function today() {
  return startOfDay(0);
}

const initialState = {
  initialCallFormPrimingInfo: null,
  idOfFirmToShow: 0,
  intendedClientId: 0,
  intendedEmergencyContactId: 0,
  intendedEmergencyContacteeId: 0,
  intendedReferrerCompanyId: 0,
  intendedReferrerContactId: 0,
  intendedDemoId: 0,
  intendedAccountId: 0,
  intendedReferralId: 0,
  intendedInquiryId: 0,
  clientsPageFilter: {},
  inquiriesPageFilter: {},
  companiesPageFilter: {
    includeHospitals: true,
    includeTreatmentCenters: true,
    includeOthers: true,
  },
  contactsPageFilter: {
    includeCompanies: true,
    includePrivatePractitioners: true,
    includeOthers: true,
    includeActive: true,
    includeInactive: false,
  },
  callLogPageFilters: {
    includeInbound: true,
    includeOutbound: true,
    startDate: yesterday().toISO(),
    // startDate: startOfDay(-24),
    endDate: today().toISO(),
  },
};

export const intendedInfoSlice = createSlice({
  name: 'intendedInfo',
  initialState: initialState,
  reducers: {
    clearIntendedInfo: () => {
      return initialState;
    },

    setFirmToShow: (state, action) => {
      return { ...state, idOfFirmToShow: action.payload };
    },

    setCaseIntendedClientId: (state, action) => {
      return { ...state, intendedClientId: action.payload };
    },

    setIntendedClientId: (state, action) => {
      return { ...state, intendedClientId: action.payload };
    },

    setIntendedEmergencyContactId: (state, action) => {
      return { ...state, intendedEmergencyContactId: action.payload };
    },

    setIntendedEmergencyContacteeId: (state, action) => {
      return { ...state, intendedEmergencyContacteeId: action.payload };
    },

    setIntendedReferrerCompanyId: (state, action) => {
      return { ...state, intendedReferrerCompanyId: action.payload };
    },

    setIntendedReferrerContactId: (state, action) => {
      return { ...state, intendedReferrerContactId: action.payload };
    },

    setDemoToShow: (state, action) => {
      return { ...state, intendedDemoId: action.payload };
    },

    setIntendedAccountId: (state, action) => {
      return { ...state, intendedAccountId: action.payload };
    },

    setIntendedReferralId: (state, action) => {
      return { ...state, intendedReferralId: action.payload };
    },

    setIntendedInquiryId: (state, action) => {
      return { ...state, intendedInquiryId: action.payload };
    },

    updateClientsFilter: (state, action) => {
      const { filterItemName, value } = action.payload;
      const newFilter = { ...state.clientsPageFilter, [filterItemName]: value };
      return { ...state, clientsPageFilter: newFilter };
    },

    removeClientsFilterKey: (state, action) => {
      const currentFilter = state.clientsPageFilter;
      const currentKeys = Object.keys(currentFilter);
      const filteredKeys = currentKeys.filter((key) => key !== action.payload);
      const newFilter = filteredKeys.reduce((accum, key) => {
        const currentValue = currentFilter[key];
        return { ...accum, [key]: currentValue };
      }, {});
      const newState = { ...state, clientsPageFilter: newFilter };
      return newState;
    },

    updateCompaniesFilter: (state, action) => {
      const { filterItemName, value } = action.payload;
      const newFilter = { ...state.companiesPageFilter, [filterItemName]: value };
      return { ...state, companiesPageFilter: newFilter };
    },

    updateContactsFilter: (state, action) => {
      const { filterItemName, value } = action.payload;
      const newFilter = { ...state.contactsPageFilter, [filterItemName]: value };
      return { ...state, contactsPageFilter: newFilter };
    },

    updateCallLogFilter: (state, action) => {
      const { filterItemName, value } = action.payload;
      const newFilter = { ...state.callLogPageFilters, [filterItemName]: value };
      return { ...state, callLogPageFilters: newFilter };
    },

    setInitialCallFormPrimingInfo: (state, action) => {
      return { ...state, initialCallFormPrimingInfo: action.payload };
    },

    clearInitialCallFormPrimingInfo: (state, action) => {
      return { ...state, initialCallFormPrimingInfo: null };
    },
  },
});

export const {
  clearIntendedInfo,
  setFirmToShow,
  setCaseIntendedClientId,
  setIntendedClientId,
  setIntendedReferrerCompanyId,
  setIntendedReferrerContactId,
  setIntendedEmergencyContactId,
  setIntendedEmergencyContacteeId,
  setDemoToShow,
  setIntendedAccountId,
  setIntendedReferralId,
  setIntendedInquiryId,
  updateClientsFilter,
  removeClientsFilterKey,
  updateCompaniesFilter,
  updateContactsFilter,
  updateCallLogFilter,
  setInitialCallFormPrimingInfo,
  clearInitialCallFormPrimingInfo,
} = intendedInfoSlice.actions;

export default intendedInfoSlice.reducer;
